import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';

const DocumentsTab = ({ employeeId }) => {
  const { user } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  // Accordion state
  const [expandedCategories, setExpandedCategories] = useState({
    'Identity & Address': true,
    'Education & Experience': false,
    'Employment Documents': false,
    'Financial': false
  });

  const fetchDocuments = async () => {
    try {
      const res = await api.get(`/employees/${employeeId}/documents`);
      setDocuments(res.data);
    } catch (err) {
      setError('Failed to fetch documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) fetchDocuments();
  }, [employeeId]);

  const handleUpload = async (e, documentTypeCode) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentTypeCode', documentTypeCode);

    setUploading(true);
    try {
      await api.post(`/employees/${employeeId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchDocuments(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
      e.target.value = null; // Reset input
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/employees/${employeeId}/documents/${doc._id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Cleanup
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('Download failed', err);
      alert('Failed to download document');
    }
  };

  const handleView = async (doc) => {
    try {
      const res = await api.get(`/employees/${employeeId}/documents/${doc._id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type }));
      window.open(url, '_blank');
      
      // Cleanup
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('View failed', err);
      alert('Failed to view document');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading documents...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  const isAdmin = user?.role === 'Admin' || user?.role === 'HR' || user?.role === 'Super Admin';

  // Group documents by category
  const groupedDocuments = documents.reduce((acc, item) => {
    const category = item.type.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  // Define the required order of categories
  const categoryOrder = [
    'Identity & Address',
    'Education & Experience',
    'Employment Documents',
    'Financial'
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
        <h3 className="font-semibold text-gray-800">Employee Documents</h3>
        {isAdmin && <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">Admin Access</span>}
      </div>
      
      <div className="divide-y divide-gray-200">
        {categoryOrder.map((category) => {
          const categoryDocs = groupedDocuments[category];
          if (!categoryDocs || categoryDocs.length === 0) return null;
          
          const isExpanded = expandedCategories[category];

          return (
            <div key={category} className="pb-0">
              <button 
                onClick={() => toggleCategory(category)}
                className="w-full px-6 py-4 bg-gray-50/50 hover:bg-gray-100 transition-colors flex items-center justify-between outline-none"
              >
                <h4 className="text-sm font-bold text-gray-700 tracking-wide">{category}</h4>
                <svg 
                  className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'transform rotate-180' : ''}`} 
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              
              {isExpanded && (
                <div className="divide-y divide-gray-100 border-t border-gray-100 bg-white">
                  {categoryDocs.map((item) => (
                    <div key={item.type.code} className="px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 hover:bg-gray-50/30 transition-colors">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{item.type.label}</span>
                        
                        {item.document ? (
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                              Uploaded
                            </span>
                            <span className="text-xs text-gray-400">• {new Date(item.document.createdAt).toLocaleDateString()}</span>
                            {item.document.uploadedBy && (
                              <span className="text-xs text-gray-400">• by {item.document.uploadedBy.firstName} {item.document.uploadedBy.lastName}</span>
                            )}
                          </div>
                        ) : (
                          <span className="mt-1 text-xs text-red-500 font-medium flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            Missing
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {item.document && (
                          <>
                            <button 
                              onClick={() => handleView(item.document)}
                              className="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors flex items-center gap-1.5"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                              View
                            </button>
                            <button 
                              onClick={() => handleDownload(item.document)}
                              className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1.5"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0l-4-4m4 4V4"></path></svg>
                              Download
                            </button>
                          </>
                        )}
                        
                        {(isAdmin || (user?.id === employeeId && category !== 'Employment Documents' && !item.document)) && (
                          <div className="relative">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleUpload(e, item.type.code)}
                              disabled={uploading}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                              id={`upload-${item.type.code}`}
                            />
                            <label 
                              htmlFor={`upload-${item.type.code}`}
                              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer
                                ${item.document 
                                  ? 'text-gray-700 bg-gray-100 hover:bg-gray-200' 
                                  : 'text-white bg-blue-600 hover:bg-blue-700'
                                } ${uploading ? 'opacity-50' : ''}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                              {item.document ? 'Replace' : 'Upload'}
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentsTab;
